'use client';

import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';

const tabsListVariants = cva('group/tabs-list text-secondary inline-flex w-fit items-center', {
  variants: {
    variant: {
      contained: 'h-9 justify-center rounded-md border border-light p-0.5',
      line: 'relative flex h-9 items-end gap-6',
      chip: 'gap-1',
      segmented: 'rounded-lg border border-light bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'contained',
  },
});

type TabsListVariant = NonNullable<VariantProps<typeof tabsListVariants>['variant']>;

// The trigger reads its variant off the list's `data-variant` rather than taking a prop of its own,
// so a trigger can never be styled for a different variant than the list it sits in.
const TABS_TRIGGER_BASE =
  "inline-flex cursor-pointer items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-border-light data-[disabled]:cursor-default data-[disabled]:text-disabled [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

// `contained` and `line` add the 1px outline the base primitive used to pair with its focus ring;
// `chip` and `segmented` never had it. All four share the base's `ring-2` — the old base rang at 3px,
// which has no scale equivalent, and 2px is the step below.
const TABS_TRIGGER_CONTAINED =
  'group-data-[variant=contained]/tabs-list:h-full group-data-[variant=contained]/tabs-list:rounded-sm group-data-[variant=contained]/tabs-list:px-3 group-data-[variant=contained]/tabs-list:not-data-disabled:hover:text-dark group-data-[variant=contained]/tabs-list:data-active:bg-muted group-data-[variant=contained]/tabs-list:data-active:text-dark group-data-[variant=contained]/tabs-list:focus-visible:border-ring group-data-[variant=contained]/tabs-list:focus-visible:outline-solid group-data-[variant=contained]/tabs-list:focus-visible:outline-1 group-data-[variant=contained]/tabs-list:focus-visible:outline-ring';

const TABS_TRIGGER_LINE =
  'group-data-[variant=line]/tabs-list:relative group-data-[variant=line]/tabs-list:z-10 group-data-[variant=line]/tabs-list:data-[disabled]:cursor-not-allowed group-data-[variant=line]/tabs-list:data-[disabled]:opacity-60 group-data-[variant=line]/tabs-list:h-full group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:border-0 group-data-[variant=line]/tabs-list:border-b-2 group-data-[variant=line]/tabs-list:border-transparent group-data-[variant=line]/tabs-list:pt-1 group-data-[variant=line]/tabs-list:pb-2.5 group-data-[variant=line]/tabs-list:not-data-disabled:hover:text-dark group-data-[variant=line]/tabs-list:data-active:border-primary group-data-[variant=line]/tabs-list:data-active:text-dark group-data-[variant=line]/tabs-list:focus-visible:border-ring group-data-[variant=line]/tabs-list:focus-visible:outline-solid group-data-[variant=line]/tabs-list:focus-visible:outline-1 group-data-[variant=line]/tabs-list:focus-visible:outline-ring';

const TABS_TRIGGER_CHIP =
  'group-data-[variant=chip]/tabs-list:rounded-md group-data-[variant=chip]/tabs-list:px-4 group-data-[variant=chip]/tabs-list:py-2 group-data-[variant=chip]/tabs-list:not-data-disabled:hover:text-dark group-data-[variant=chip]/tabs-list:data-active:bg-muted group-data-[variant=chip]/tabs-list:data-active:text-dark';

// A wrapping list has no single row height for `h-full` to resolve against, so the trigger has to
// size itself instead.
const TABS_TRIGGER_WRAP = 'group-data-[wrap]/tabs-list:h-auto group-data-[wrap]/tabs-list:py-1';

const TABS_TRIGGER_SEGMENTED =
  'group-data-[variant=segmented]/tabs-list:h-10 group-data-[variant=segmented]/tabs-list:border-r group-data-[variant=segmented]/tabs-list:border-light group-data-[variant=segmented]/tabs-list:px-4 group-data-[variant=segmented]/tabs-list:first:rounded-l-sm group-data-[variant=segmented]/tabs-list:last:rounded-r-sm group-data-[variant=segmented]/tabs-list:last:border-r-0 group-data-[variant=segmented]/tabs-list:not-data-disabled:hover:text-dark group-data-[variant=segmented]/tabs-list:data-active:bg-primary/10 group-data-[variant=segmented]/tabs-list:data-active:text-primary';

function Tabs({ className, ...props }: ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn('flex flex-col gap-2', className)} {...props} />;
}

function TabsList({
  className,
  variant = 'contained',
  wrap = false,
  ...props
}: ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants> & {
    /**
     * Let the tabs break onto another row instead of overflowing their container. Off by default
     * because a fixed-height single row is what most callers want; turn it on where the labels are
     * long enough to outgrow the available width.
     */
    readonly wrap?: boolean;
  }) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-variant={variant}
      // Presence, not `false`, so `group-data-[wrap]` on the trigger matches only when wrapping.
      data-wrap={wrap ? '' : undefined}
      // `justify-start` because the variant centres its single row, which leaves a wrapped last row
      // floating in the middle of the track instead of lining up under the first.
      className={cn(tabsListVariants({ variant }), wrap && 'h-auto flex-wrap justify-start gap-y-0.5', className)}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Tab>) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        TABS_TRIGGER_BASE,
        TABS_TRIGGER_CONTAINED,
        TABS_TRIGGER_LINE,
        TABS_TRIGGER_CHIP,
        TABS_TRIGGER_SEGMENTED,
        TABS_TRIGGER_WRAP,
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Panel>) {
  return <TabsPrimitive.Panel data-slot="tabs-content" className={cn('flex-1 outline-none', className)} {...props} />;
}

function TabsToggleList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsList variant="chip" data-slot="tabs-toggle-list" className={className} {...props} />;
}

function TabsToggleTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Tab>) {
  return <TabsTrigger data-slot="tabs-toggle-trigger" className={className} {...props} />;
}

function SegmentedToggleList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsList variant="segmented" data-slot="tabs-segmented-list" className={className} {...props} />;
}

function SegmentedToggleTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Tab>) {
  return <TabsTrigger data-slot="tabs-segmented-trigger" className={className} {...props} />;
}

type TabVariant = 'link' | 'toggle';
const TAB_VARIANT_LINK: TabVariant = 'link';
const TAB_VARIANT_TOGGLE: TabVariant = 'toggle';

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsToggleList,
  TabsToggleTrigger,
  SegmentedToggleList,
  SegmentedToggleTrigger,
  tabsListVariants,
  TAB_VARIANT_LINK,
  TAB_VARIANT_TOGGLE,
};
export type { TabVariant, TabsListVariant };
